<template>
  <div class="retention-analysis-table-cont">
    <t-enhanced-table
      ref="table"
      row-key="id"
      :columns="columns"
      :data="data"
      :pagination="pagination"
    ></t-enhanced-table>
  </div>
</template>

<script>
import lodash from 'lodash';
import numeral from 'numeral';
import { CaretDownSmallIcon, CaretRightSmallIcon } from 'tdesign-icons-vue';
import dayjs from 'dayjs';
import { DEFAULT_TIME_UNIT_ENUM } from '@/views/analysis/retention-analysis/components/config';

export default {
  name: 'RetentionDataTable',
  props: {
    analysisData: {
      type: Array,
      default: () => [],
    },
    timeUnit: {
      type: String,
    },
    followStartDay: {
      type: Number,
    },
    followEndDay: {
      type: Number,
    },
    isCalcToday: {
      type: Number,
    },
  },
  data() {
    return {
      columns: [],
      data: [],

      pagination: {
        defaultCurrent: 1,
        defaultPageSize: 10,
        total: 0,
      },
    };
  },
  watch: {
    analysisData: {
      handler() {
        this.translateAnalysisData();
      },
      deep: true,
    },
  },
  mounted() {
    this.columns = this.generatedColumn();
    this.data = this.translateAnalysisData();
    this.pagination.total = this.data.length;
  },
  methods: {
    translateAnalysisData() {
      const { analysisData, timeUnit } = this;
      if (!analysisData || !analysisData.length) {
        return [];
      }
      const region = this.getRegion();

      let dataArr = [];
      analysisData.forEach((analysisDataItem, analysisDataItemIndex) => {
        const subDataArr = analysisDataItem.dataList.map((item, itemIndex) => {
          const row = {
            id: `${analysisDataItemIndex}-${itemIndex}`,
            eventName: `${item.eventChineseName || item.eventName} ${item.showLabel}${itemIndex}`,
            globalFilerName: item.globalFilerName,
            group: item.groupBy,
            total: item.summary[1],
            showChildren: false,
          };

          if (region.length) {
            const values = item.summary.slice(2, item.summary.length);
            for (let i = 0; i < region.length; i++) {
              row[`item${i}`] = values[i];
            }
          }

          const children = item.details.map((detail, subIndex) => {
            const childRow = {
              id: `${analysisDataItemIndex}-${itemIndex}-${subIndex}`,
              eventName: this.getEventNameText(detail[0]),
              globalFilerName: item.globalFilerName,
              group: item.groupBy,
              total: detail[1],
            };

            if (region.length) {
              const detailValues = detail.slice(2, detail.length);
              for (let i = 0; i < region.length; i++) {
                childRow[`item${i}`] = detailValues[i];
              }
            }

            return childRow;
          });

          row.children = children;
          return row;
        });

        dataArr = dataArr.concat(subDataArr);
      });
      return dataArr;
    },
    generatedColumn() {
      const currColumns = this.getBaseColumns();

      const { analysisData } = this;
      if (!analysisData || !analysisData.length) {
        return currColumns;
      }

      const region = this.getRegion();

      if (!region.length) {
        return currColumns;
      }
      for (let i = 0; i < region.length; i++) {
        currColumns.push({
          colKey: `item${i}`,
          title: this.getRegionItemText(region[i]),
          width: 100,
          className: 'analysis-data-cell',
          cell: (h, { col, row }) => {
            const value = row[col.colKey];
            if (!value || value.length !== 2) {
              return null;
            }
            return (
              <div class={`analysis-data cell-bg-${this.ratioLevel(value[1])}`}>
                <p>{this.numFormat(value[0])}</p>
                <p>{this.ratioFormat(value[1])}</p>
              </div>
            );
          },
        });
      }
      return currColumns;
    },
    getBaseColumns() {
      let showGroup = false;
      if (this.analysisData) {
        this.analysisData.forEach(item => {
          item.dataList.forEach(subItem => {
            if (subItem.groupBy) {
              showGroup = true;
            }
          });
        });
      }
      const columns = [
        {
          colKey: 'expand',
          title: '',
          width: 40,
          cell: (h, p) => {
            const { col, row } = p;
            if (row.children && row.children.length) {
              const Icon = row.showChildren ? CaretDownSmallIcon : CaretRightSmallIcon;
              return h(Icon, {
                on: {
                  click: () => {
                    row.showChildren = !row.showChildren;
                    this.$refs.table.toggleExpandData(p);
                  },
                },
              });
            }
            return null;
          },
        },
        { colKey: 'eventName', title: '事件/日期', width: 160 },
        { colKey: 'globalFilerName', title: '全局筛选', width: 110 },
      ];
      if (showGroup) {
        columns.push({ colKey: 'group', title: '分组', width: 100 });
      }
      columns.push({
        colKey: 'total',
        title: '起始人数',
        width: 100,
        className: 'analysis-data-cell',
        cell: (h, { col, row }) => (
          <div class="analysis-data">
            <p>{this.numFormat(row[col.colKey])}</p>
            <p></p>
          </div>
        ),
      });
      return lodash.cloneDeep(columns);
    },
    getRegion() {
      const { dataList } = this.analysisData[0];
      const { isCalcToday, followStartDay, followEndDay, timeUnit } = this;
      const region = [];

      if (timeUnit === 'day') {
        if (isCalcToday) {
          region.push(1);
        }
        for (let i = followStartDay; i <= followEndDay; i++) {
          region.push(i);
        }
      }
      if (timeUnit === 'week' || timeUnit === 'month') {
        const totalNum = dataList[0].summary.length - 2;
        let followStart = isCalcToday ? 1 : 2;
        for (let i = 0; i < totalNum; i++) {
          region.push(followStart);
          followStart += 1;
        }
      }
      return region;
    },
    getRegionItemText(regionItem) {
      return regionItem === 1
        ? `当${DEFAULT_TIME_UNIT_ENUM[this.timeUnit]}`
        : `第${regionItem}${DEFAULT_TIME_UNIT_ENUM[this.timeUnit]}`;
    },
    getEventNameText(eventName) {
      const { timeUnit } = this;
      if (timeUnit === 'day') {
        return dayjs(eventName).format('MM-DD');
      }
      if (timeUnit === 'week') {
        const arr = eventName.split('-');
        return `${dayjs(arr[0]).format('MM-DD')}~${dayjs(arr[1]).format('MM-DD')}`;
      }
      const arr = eventName.split('-');
      return dayjs(arr[0]).format('YYYY-MM');
    },
    numFormat(num) {
      return numeral(num).format('0,0');
    },
    ratioFormat(num) {
      return numeral(num).format('0.00%');
    },
    ratioLevel(ratio) {
      if (ratio >= 0.8) {
        return 5;
      }
      if (ratio >= 0.5) {
        return 4;
      }
      if (ratio >= 0.2) {
        return 3;
      }
      if (ratio >= 0.01) {
        return 2;
      }
      return 1;
    },
  },
};
</script>

<style scoped lang="less">
.retention-analysis-table-cont {
  width: 100%;
  overflow-y: auto;

  ::v-deep .t-table {
    width: 99%;

    thead td {
      font-weight: 500;
      &.t-table__expandable-icon-cell {
        position: sticky;
        left: 0;
      }
    }
    .t-table__expanded-row td {
      padding: 0;
    }
    .t-table__tree-col svg:first-child {
      display: none;
    }
    tbody td {
      height: 74px;

      &.analysis-data-cell {
        padding: 0;
        border-right: 1px solid #e7e7e7;

        .analysis-data {
          box-sizing: border-box;
          padding: 12px 7px 11px 7px;
          position: relative;

          &:after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 8px;
          }

          &.cell-bg-1:after {
            background-color: #dddddd;
          }
          &.cell-bg-2:after {
            background-color: #c9e4ff;
          }
          &.cell-bg-3:after {
            background-color: #60a7ff;
          }
          &.cell-bg-4:after {
            background-color: #348bff;
          }
          &.cell-bg-5:after {
            background-color: #0d6ce4;
          }

          p {
            margin: 0;
            width: 100%;
            height: 21px;
            line-height: 21px;
            font-weight: 700;
            font-size: 14px;
            color: #333;
            text-align: right;
            &:first-child {
              margin-bottom: 8px;
            }
          }
        }
      }
    }
  }
}
</style>
